import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, Filter, Grid, List } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Format definitions with dynamic counts (loaded from API)
const INITIAL_FORMATS = [
  { id: "current", name: "Actual (Todas)", count: null, color: "from-purple-500 to-pink-500" },
  { id: "edison", name: "Edison (Abril 2010)", count: null, color: "from-yellow-500 to-amber-600" },
  { id: "goat", name: "GOAT (Abril 2005)", count: null, color: "from-green-500 to-emerald-600" },
  { id: "hat", name: "HAT (Julio 2014)", count: null, color: "from-purple-500 to-indigo-600" }
];

// Card component
const FormatCard = ({ card, onClick, formatId }) => {
  const status = card.format_status || card.edison_status;
  
  const getStatusColor = (status) => {
    if (status === "Forbidden") return "border-red-500 bg-red-500/20";
    if (status === "Limited") return "border-yellow-500 bg-yellow-500/20";
    if (status === "Semi-Limited") return "border-green-500 bg-green-500/20";
    return "border-gray-600 bg-gray-800/50";
  };

  const getStatusBadge = (status) => {
    if (status === "Forbidden") return "bg-red-500 text-white";
    if (status === "Limited") return "bg-yellow-500 text-black";
    if (status === "Semi-Limited") return "bg-green-500 text-white";
    return null;
  };

  const getFrameColor = (frameType) => {
    if (frameType?.includes("spell")) return "from-green-600 to-teal-700";
    if (frameType?.includes("trap")) return "from-pink-600 to-purple-700";
    if (frameType?.includes("fusion")) return "from-purple-600 to-violet-700";
    if (frameType?.includes("synchro")) return "from-gray-200 to-gray-400";
    if (frameType?.includes("xyz")) return "from-gray-800 to-black";
    if (frameType?.includes("ritual")) return "from-blue-600 to-indigo-700";
    return "from-yellow-600 to-orange-600";
  };

  const imageUrl = card.card_images?.[0]?.image_url_small || 
                   `https://images.ygoprodeck.com/images/cards_small/${card.id}.jpg`;

  return (
    <div 
      className={`relative group cursor-pointer rounded-lg border-2 ${getStatusColor(status)} p-1 transition-all hover:scale-105 hover:shadow-xl`}
      onClick={() => onClick(card)}
      data-testid={`format-card-${card.id}`}
    >
      {/* Status Badge - only for format cards */}
      {status && status !== "Unlimited" && formatId !== "current" && (
        <div className={`absolute -top-2 -right-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusBadge(status)}`}>
          {status === "Forbidden" ? "🚫" : status === "Limited" ? "1" : "2"}
        </div>
      )}
      
      <div className={`aspect-[421/614] rounded overflow-hidden bg-gradient-to-br ${getFrameColor(card.frameType)}`}>
        <img 
          src={imageUrl}
          alt={card.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://placehold.co/100x145/1a1a2e/ffffff?text=${encodeURIComponent(card.name?.substring(0, 10) || 'Card')}`;
          }}
        />
      </div>
      
      <p className="text-[10px] text-gray-300 text-center mt-1 truncate px-1" title={card.name}>
        {card.name}
      </p>
    </div>
  );
};

// Card Detail Modal
const CardDetailModal = ({ card, onClose, formatId }) => {
  if (!card) return null;

  const status = card.format_status || card.edison_status;
  const imageUrl = card.card_images?.[0]?.image_url || 
                   `https://images.ygoprodeck.com/images/cards/${card.id}.jpg`;

  const getStatusColor = (status) => {
    if (status === "Forbidden") return "bg-red-500";
    if (status === "Limited") return "bg-yellow-500 text-black";
    if (status === "Semi-Limited") return "bg-green-500";
    return "bg-gray-600";
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-4 sm:p-6 max-w-2xl w-full border border-purple-700 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors">
          ✕
        </button>

        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <div className="w-full sm:w-48 flex-shrink-0 mx-auto sm:mx-0">
            <img src={imageUrl} alt={card.name} className="w-full rounded-lg shadow-xl" onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/300x420/1a1a2e/ffffff?text=${encodeURIComponent(card.name?.substring(0, 15) || 'Card')}`; }} />
          </div>

          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2">{card.name}</h3>
            
            <div className="flex flex-wrap gap-2 mb-3">
              {status && formatId !== "current" && (
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(status)}`}>
                  {status}
                </span>
              )}
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-600 text-white">
                {card.type}
              </span>
            </div>

            {(card.atk !== undefined || card.def !== undefined) && (
              <div className="flex gap-4 mb-3 text-sm">
                {card.level && <div className="flex items-center gap-1"><span className="text-yellow-400">★</span><span className="text-gray-300">Level {card.level}</span></div>}
                {card.atk !== undefined && <div><span className="text-red-400 font-bold">ATK</span><span className="text-gray-300 ml-1">{card.atk}</span></div>}
                {card.def !== undefined && <div><span className="text-blue-400 font-bold">DEF</span><span className="text-gray-300 ml-1">{card.def}</span></div>}
              </div>
            )}

            {(card.attribute || card.race) && (
              <div className="flex gap-2 mb-3 text-xs">
                {card.attribute && <span className="px-2 py-1 bg-gray-800 rounded text-gray-400">{card.attribute}</span>}
                {card.race && <span className="px-2 py-1 bg-gray-800 rounded text-gray-400">{card.race}</span>}
              </div>
            )}

            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <p className="text-sm text-gray-300 leading-relaxed">{card.desc}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CardDatabase = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFormat, setSelectedFormat] = useState(searchParams.get('format') || "edison");
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [cardType, setCardType] = useState("");
  const [attribute, setAttribute] = useState("");
  const [monsterType, setMonsterType] = useState("");
  const [level, setLevel] = useState("");
  const [atkMin, setAtkMin] = useState("");
  const [atkMax, setAtkMax] = useState("");
  const [defMin, setDefMin] = useState("");
  const [defMax, setDefMax] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(true);
  const [formats, setFormats] = useState(INITIAL_FORMATS);
  const cardsPerPage = 100;

  // Load format counts on mount
  useEffect(() => {
    const loadFormatCounts = async () => {
      try {
        const updatedFormats = await Promise.all(
          INITIAL_FORMATS.map(async (format) => {
            try {
              const url = format.id === "current" 
                ? `${API}/cards?limit=1`
                : `${API}/cards/by-format/${format.id}?limit=1`;
              const res = await axios.get(url);
              return { ...format, count: res.data.total || 0 };
            } catch (e) {
              console.error(`Error loading count for ${format.id}:`, e);
              return format;
            }
          })
        );
        setFormats(updatedFormats);
      } catch (e) {
        console.error("Error loading format counts:", e);
      }
    };
    loadFormatCounts();
  }, []);

  const loadCards = useCallback(async (newPage = 0) => {
    setLoading(true);
    try {
      let url;
      if (selectedFormat === "current") {
        url = `${API}/cards?skip=${newPage * cardsPerPage}&limit=${cardsPerPage}`;
      } else {
        url = `${API}/cards/by-format/${selectedFormat}?skip=${newPage * cardsPerPage}&limit=${cardsPerPage}`;
      }
      
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (cardType) url += `&card_type=${cardType}`;
      if (attribute) url += `&attribute=${attribute}`;
      if (level) url += `&level=${level}`;
      if (monsterType) url += `&race=${encodeURIComponent(monsterType)}`;
      if (atkMin) url += `&atk_min=${atkMin}`;
      if (atkMax) url += `&atk_max=${atkMax}`;
      if (defMin) url += `&def_min=${defMin}`;
      if (defMax) url += `&def_max=${defMax}`;
      
      const res = await axios.get(url);
      setCards(res.data.cards || []);
      setTotal(res.data.total || 0);
      setPage(newPage);
    } catch (e) {
      console.error("Error loading cards:", e);
    }
    setLoading(false);
  }, [selectedFormat, searchTerm, cardType, attribute, monsterType, level, atkMin, atkMax, defMin, defMax, cardsPerPage]);

  useEffect(() => {
    loadCards();
  }, [selectedFormat, loadCards]);

  const handleFormatChange = (formatId) => {
    setSelectedFormat(formatId);
    setSearchParams({ format: formatId });
    setPage(0);
  };
  
  const clearFilters = () => {
    setSearchTerm("");
    setCardType("");
    setAttribute("");
    setMonsterType("");
    setLevel("");
    setAtkMin("");
    setAtkMax("");
    setDefMin("");
    setDefMax("");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadCards(0);
  };

  const currentFormat = formats.find(f => f.id === selectedFormat) || formats[0];
  const totalPages = Math.ceil(total / cardsPerPage);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-950/30 to-black pt-24 pb-12" data-testid="card-database-page">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Link to="/formatos" className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors text-sm mb-4">
            <ArrowLeft size={16} /> Volver a Formatos
          </Link>
          
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-black mb-2">
              <span className={`bg-gradient-to-r ${currentFormat.color} bg-clip-text text-transparent`}>
                Base de Datos de Cartas
              </span>
            </h1>
            <p className="text-gray-400 text-sm">{currentFormat.name}</p>
          </div>
        </div>

        {/* Format Selector - Pills */}
        <div className="max-w-4xl mx-auto mb-6">
          <div className="flex flex-wrap justify-center gap-2">
            {formats.map(format => (
              <button
                key={format.id}
                onClick={() => handleFormatChange(format.id)}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                  selectedFormat === format.id
                    ? `bg-gradient-to-r ${format.color} text-white shadow-lg`
                    : 'bg-purple-900/50 text-gray-400 hover:text-white hover:bg-purple-800/50'
                }`}
                data-testid={`format-btn-${format.id}`}
              >
                {format.name}
                <span className="ml-2 opacity-75">
                  ({format.count !== null ? format.count.toLocaleString() : '...'})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="max-w-4xl mx-auto mb-6">
          <div className="glass-card rounded-xl p-4 flex items-center justify-between">
            <div className="text-center flex-1">
              <p className="text-2xl font-black text-white">{total.toLocaleString()}</p>
              <p className="text-gray-400 text-xs">Cartas</p>
            </div>
            <div className="h-8 w-px bg-purple-700/50"></div>
            <div className="text-center flex-1">
              <p className="text-sm font-bold text-yellow-400">{currentFormat.name}</p>
              <p className="text-gray-400 text-xs">Formato</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="max-w-4xl mx-auto mb-6">
          <div className="glass-card rounded-xl p-4">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar carta por nombre..."
                  className="w-full pl-10 pr-4 py-2.5 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                />
              </div>
              
              <button type="button" onClick={() => setShowFilters(!showFilters)} className="px-4 py-2.5 bg-purple-800/50 border border-purple-700/50 rounded-lg text-gray-300 hover:text-white transition-colors flex items-center gap-2">
                <Filter size={18} /> Filtros
              </button>
              
              <button type="submit" className={`px-6 py-2.5 bg-gradient-to-r ${currentFormat.color} rounded-lg font-bold text-white hover:shadow-lg transition-all`}>
                Buscar
              </button>
            </form>

            {showFilters && (
              <div className="mt-4 pt-4 border-t border-purple-700/50">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-3">
                  {/* Card Type */}
                  <select 
                    value={cardType} 
                    onChange={(e) => setCardType(e.target.value)} 
                    className="px-3 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500"
                    data-testid="filter-card-type"
                  >
                    <option value="">Tipo de Carta</option>
                    <option value="monster">Monstruo</option>
                    <option value="spell">Magia</option>
                    <option value="trap">Trampa</option>
                  </select>
                  
                  {/* Monster Type */}
                  <select 
                    value={monsterType} 
                    onChange={(e) => setMonsterType(e.target.value)} 
                    className="px-3 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500"
                    data-testid="filter-monster-type"
                  >
                    <option value="">Tipo de Monstruo</option>
                    <option value="Dragon">Dragon</option>
                    <option value="Warrior">Warrior</option>
                    <option value="Spellcaster">Spellcaster</option>
                    <option value="Fiend">Fiend</option>
                    <option value="Machine">Machine</option>
                    <option value="Zombie">Zombie</option>
                    <option value="Beast">Beast</option>
                    <option value="Beast-Warrior">Beast-Warrior</option>
                    <option value="Dinosaur">Dinosaur</option>
                    <option value="Fairy">Fairy</option>
                    <option value="Insect">Insect</option>
                    <option value="Aqua">Aqua</option>
                    <option value="Pyro">Pyro</option>
                    <option value="Thunder">Thunder</option>
                    <option value="Rock">Rock</option>
                    <option value="Plant">Plant</option>
                    <option value="Winged Beast">Winged Beast</option>
                    <option value="Reptile">Reptile</option>
                    <option value="Fish">Fish</option>
                    <option value="Sea Serpent">Sea Serpent</option>
                    <option value="Psychic">Psychic</option>
                    <option value="Wyrm">Wyrm</option>
                    <option value="Cyberse">Cyberse</option>
                  </select>
                  
                  {/* Attribute */}
                  <select 
                    value={attribute} 
                    onChange={(e) => setAttribute(e.target.value)} 
                    className="px-3 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500"
                    data-testid="filter-attribute"
                  >
                    <option value="">Atributo</option>
                    <option value="DARK">DARK</option>
                    <option value="LIGHT">LIGHT</option>
                    <option value="EARTH">EARTH</option>
                    <option value="WATER">WATER</option>
                    <option value="FIRE">FIRE</option>
                    <option value="WIND">WIND</option>
                    <option value="DIVINE">DIVINE</option>
                  </select>
                  
                  {/* Level */}
                  <select 
                    value={level} 
                    onChange={(e) => setLevel(e.target.value)} 
                    className="px-3 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500"
                    data-testid="filter-level"
                  >
                    <option value="">Nivel/Rank</option>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(l => (
                      <option key={l} value={l}>Nivel {l}</option>
                    ))}
                  </select>
                  
                  {/* ATK Range */}
                  <div className="flex gap-1">
                    <input
                      type="number"
                      value={atkMin}
                      onChange={(e) => setAtkMin(e.target.value)}
                      placeholder="ATK min"
                      className="w-full px-2 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500"
                      data-testid="filter-atk-min"
                    />
                    <input
                      type="number"
                      value={atkMax}
                      onChange={(e) => setAtkMax(e.target.value)}
                      placeholder="ATK max"
                      className="w-full px-2 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500"
                      data-testid="filter-atk-max"
                    />
                  </div>
                  
                  {/* DEF Range */}
                  <div className="flex gap-1">
                    <input
                      type="number"
                      value={defMin}
                      onChange={(e) => setDefMin(e.target.value)}
                      placeholder="DEF min"
                      className="w-full px-2 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500"
                      data-testid="filter-def-min"
                    />
                    <input
                      type="number"
                      value={defMax}
                      onChange={(e) => setDefMax(e.target.value)}
                      placeholder="DEF max"
                      className="w-full px-2 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500"
                      data-testid="filter-def-max"
                    />
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2 justify-end">
                  <button 
                    type="button" 
                    onClick={() => loadCards(0)} 
                    className="px-4 py-2 bg-purple-700 rounded-lg text-white font-semibold hover:bg-purple-600 transition-colors text-sm"
                    data-testid="apply-filters-btn"
                  >
                    Aplicar Filtros
                  </button>
                  
                  <button 
                    type="button" 
                    onClick={() => { clearFilters(); loadCards(0); }} 
                    className="px-4 py-2 bg-gray-700 rounded-lg text-gray-300 hover:text-white transition-colors text-sm"
                    data-testid="clear-filters-btn"
                  >
                    Limpiar Filtros
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results Info */}
        <div className="max-w-7xl mx-auto mb-4 flex items-center justify-between">
          <p className="text-gray-400 text-sm">
            Mostrando {page * cardsPerPage + 1} - {Math.min((page + 1) * cardsPerPage, total)} de {total.toLocaleString()}
          </p>
          
          <div className="flex items-center gap-2">
            <button onClick={() => setViewMode("grid")} className={`p-2 rounded-lg transition-colors ${viewMode === "grid" ? "bg-yellow-500 text-black" : "bg-purple-900/50 text-gray-400 hover:text-white"}`}>
              <Grid size={18} />
            </button>
            <button onClick={() => setViewMode("list")} className={`p-2 rounded-lg transition-colors ${viewMode === "list" ? "bg-yellow-500 text-black" : "bg-purple-900/50 text-gray-400 hover:text-white"}`}>
              <List size={18} />
            </button>
          </div>
        </div>

        {/* Cards Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className={`w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4 border-yellow-500`}></div>
            <p className="text-gray-400">Cargando cartas...</p>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto">
            {viewMode === "grid" ? (
              <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2">
                {cards.map((card) => (
                  <FormatCard key={card.id} card={card} onClick={setSelectedCard} formatId={selectedFormat} />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {cards.map((card) => {
                  const status = card.format_status || card.edison_status;
                  return (
                    <div key={card.id} onClick={() => setSelectedCard(card)} className="glass-card rounded-lg p-3 flex items-center gap-4 cursor-pointer hover:bg-purple-900/30 transition-colors">
                      <img src={card.card_images?.[0]?.image_url_small || `https://images.ygoprodeck.com/images/cards_small/${card.id}.jpg`} alt={card.name} className="w-12 h-16 object-cover rounded" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-white truncate">{card.name}</h4>
                        <p className="text-gray-400 text-sm truncate">{card.type}</p>
                      </div>
                      {status && status !== "Unlimited" && selectedFormat !== "current" && (
                        <span className={`px-2 py-1 rounded text-xs font-bold ${status === "Forbidden" ? "bg-red-500 text-white" : status === "Limited" ? "bg-yellow-500 text-black" : "bg-green-500 text-white"}`}>
                          {status}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="max-w-7xl mx-auto mt-6 flex items-center justify-center gap-2">
            <button onClick={() => loadCards(page - 1)} disabled={page === 0} className="px-4 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-800/50 transition-colors">
              Anterior
            </button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i;
                else if (page < 3) pageNum = i;
                else if (page > totalPages - 4) pageNum = totalPages - 5 + i;
                else pageNum = page - 2 + i;
                
                return (
                  <button key={pageNum} onClick={() => loadCards(pageNum)} className={`w-10 h-10 rounded-lg font-semibold transition-colors ${page === pageNum ? "bg-yellow-500 text-black" : "bg-purple-900/50 text-gray-400 hover:text-white"}`}>
                    {pageNum + 1}
                  </button>
                );
              })}
            </div>
            
            <button onClick={() => loadCards(page + 1)} disabled={page >= totalPages - 1} className="px-4 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-800/50 transition-colors">
              Siguiente
            </button>
          </div>
        )}
      </div>

      {selectedCard && <CardDetailModal card={selectedCard} onClose={() => setSelectedCard(null)} formatId={selectedFormat} />}
    </div>
  );
};

export default CardDatabase;

// Also export as EdisonCardDatabase for backwards compatibility
export { CardDatabase as EdisonCardDatabase };
